import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BarChart2,
  CheckSquare,
  FileText,
  LayoutGrid,
  LogOut,
  MessageSquare,
} from 'lucide-react';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BUTTON_PRIMARY } from '@/shared/ui-class';
import { GlassCard, Pill } from '@/design-system';
import {
  SEED_LOBBY_PANELS,
  type LobbyPanel,
  type LobbyPanelSurface,
} from '@/versions/v1/shared/admin-seed';
import { SEED_USER } from '@/versions/v1/shared/user-seed';
import { AuthSplitShell } from './AuthSplitShell';

const PANEL_ICONS: Record<LobbyPanelSurface, typeof LayoutGrid> = {
  tasks: CheckSquare,
  docs: FileText,
  chat: MessageSquare,
  data: BarChart2,
  generic: LayoutGrid,
};

const ADMIT_DELAY_MS = 8_000;
const CAROUSEL_INTERVAL_MS = 5_000;

function periodForHour(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function sanitizeFirstName(name: string | null): string | null {
  if (!name?.trim()) return null;
  const trimmed = name.trim();
  if (trimmed.includes('@')) {
    const local = trimmed.split('@')[0]?.trim();
    return local || null;
  }
  return trimmed.split(/\s+/)[0] ?? null;
}

function greetingText(
  period: 'morning' | 'afternoon' | 'evening' | null,
  firstName: string | null,
): string {
  if (!period) return firstName ? `Welcome, ${firstName}` : 'Welcome';
  if (!firstName) return 'Welcome';
  return `Good ${period}, ${firstName}`;
}

function LobbyGreeting({ name }: { name: string | null }) {
  const firstName = sanitizeFirstName(name);
  const greeting = useMemo(() => {
    const period = periodForHour(new Date().getHours());
    return greetingText(period, firstName);
  }, [firstName]);

  return (
    <p className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
      {greeting}
    </p>
  );
}

function LobbyCarousel({ panels }: { panels: LobbyPanel[] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (panels.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % panels.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [panels.length, activeIndex]);

  if (panels.length === 0) return null;

  const panel = panels[activeIndex]!;
  const Icon = PANEL_ICONS[panel.surface] ?? LayoutGrid;

  return (
    <div aria-label="What's inside" role="region" className="mt-7 text-left">
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">
        What&apos;s inside
      </p>
      <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] p-3.5 shadow-[inset_0_1px_2px_0_rgba(20,15,12,0.05)]">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[var(--glass-border)] bg-[var(--glass-surface-strong)] text-accent-600">
            <Icon className="h-3.5 w-3.5" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="line-clamp-1 text-sm font-medium leading-snug text-zinc-900 dark:text-zinc-100">
              {panel.title}
            </p>
            <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted">
              {panel.body}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-center gap-1.5">
        {panels.map((entry, index) => (
          <button
            key={`${entry.title}-${index}`}
            type="button"
            aria-label={`Show panel ${index + 1}`}
            aria-current={index === activeIndex}
            onClick={() => setActiveIndex(index)}
            className={cn(
              'h-1.5 w-1.5 rounded-full transition-colors',
              index === activeIndex
                ? 'bg-accent-600'
                : 'bg-zinc-300 dark:bg-zinc-600',
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function LobbyScene() {
  const navigate = useNavigate();
  const [admitted, setAdmitted] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAdmitted(true), ADMIT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const signOut = () => navigate('/login?reason=session-ended');

  return (
    <Commentable name="LobbyScene">
      <AuthSplitShell
        hero={
          <aside className="auth-hero hidden border-r border-[var(--hairline)] bg-[var(--surface-1)] p-12 md:flex md:flex-col md:justify-center">
            <div className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Optiflow
            </div>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted">
              {admitted
                ? 'Your account is approved — head to the workspace to get started.'
                : 'Your signup is queued. An admin will review it shortly.'}
            </p>
          </aside>
        }
      >
        <GlassCard tone="strong" className="w-full max-w-md p-8 text-center">
          {admitted ? (
            <>
              <Pill tone="success" className="mb-4">
                Admitted
              </Pill>
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
                You&apos;re in — continue to workspace
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-muted">
                Your account has been approved. Head to the workspace to get started.
              </p>
              <Link to="/" className={cn(BUTTON_PRIMARY, 'mt-8 inline-flex')}>
                Continue to workspace
              </Link>
              <button
                type="button"
                onClick={signOut}
                className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-muted underline-offset-4 transition-colors hover:text-foreground hover:underline"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2">
                <Pill tone="warning" className="mb-0">
                  Waiting for approval
                </Pill>
                <span aria-hidden className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/70" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
                </span>
              </div>

              <div className="mt-4">
                <LobbyGreeting name={SEED_USER.name} />
              </div>

              <p className="mt-3 text-base font-medium tracking-tight text-zinc-900 dark:text-zinc-100">
                You&apos;re on the list
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                Your account is set up and waiting for an administrator to admit you.
                Nothing more to do on your end.
              </p>
            <p className="mt-2 text-xs text-muted">
              We&apos;ll email you the moment you&apos;re in.
            </p>

              <LobbyCarousel panels={SEED_LOBBY_PANELS} />

              <div className="mt-8 border-t border-[var(--glass-border)] pt-5">
                <p className="text-center text-xs text-muted">
                  Need access sooner? Reach the admin at{' '}
                  <a
                    href="mailto:admin@example.com"
                    className="font-medium text-accent-700 dark:text-accent-400"
                  >
                    admin@example.com
                  </a>
                  .
                </p>
                <button
                  type="button"
                  onClick={signOut}
                  className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-muted underline-offset-4 transition-colors hover:text-foreground hover:underline"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </button>
              </div>
            </>
          )}
        </GlassCard>
      </AuthSplitShell>
    </Commentable>
  );
}

export function LobbyDeclinedScene() {
  return (
    <Commentable name="LobbyDeclinedScene">
      <AuthSplitShell
        hero={
          <aside className="auth-hero hidden border-r border-[var(--hairline)] bg-[var(--surface-1)] p-12 md:flex md:flex-col md:justify-center">
            <div className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
              Optiflow
            </div>
          </aside>
        }
      >
        <GlassCard tone="strong" className="w-full max-w-md p-8 text-center opacity-90">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">
            Your request wasn&apos;t approved
          </h1>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted">
            Your account request didn&apos;t go through this time. If you think this is a
            mistake, reach out to the admin at{' '}
            <a href="mailto:admin@example.com" className="font-medium text-accent-700 dark:text-accent-400">
              admin@example.com
            </a>
            .
          </p>
        </GlassCard>
      </AuthSplitShell>
    </Commentable>
  );
}
